import { initializeIcons, mergeStyles } from "@fluentui/react";
import { TestCatalog } from "./services/TestCatalog";
import { StringsService } from "./services/StringsService";
import { SettingsService } from "./services/SettingsService";
import { TestResultRepository } from "./services/TestResultRepository";
import { ProofOfWorkService } from "./services/ProofOfWorkService";
import { OAuthService } from "./services/OAuthService";
import { CookieConsentService } from "./services/CookieConsentService";
import { TrustedTypePolicyManager } from "./services/TrustedTypePolicyManager";
import { renderApp } from "./App";
import { registerCustomIcons } from "./registerCustomIcons";
import { AntiforgeryService } from "./services/AntiforgeryService";
import { applyTheme, toThemeName } from "./services/ThemeService";

/**
 * The boot configuration (from server)
 */
export interface IRcaBootConfig {
  /**
   * The binary version of the application
   */
  binaryVersion: string;

  /**
   * The advertised version of the application
   */
  appVersion: string;

  /**
   * The language
   */
  language: string;

  /**
   * The language direction (rtl or ltr)
   */
  langDir: string;

  /**
   * The email address to use for feedback
   */
  feedbackEmail: string;

  /**
   * The anti-forgery token
   */
  antiForgeryToken: string;

  /**
   * The OAuth authority
   */
  oauthAuthority: string;

  /**
   * The OAuth client ID
   */
  oauthClientId: string;

  /**
   * The OAuth redirect URI
   */
  oauthRedirectUri: string;

  /**
   * The OAuth scope to acquire tokens for
   */
  oauthScope: string;

  /**
   * Banner dismissal timestamps
   */
  bannersDismissed: Record<string, string>;

  /**
   * The user preferences (from the server)
   */
  preferences: Record<string, string>;
}

/**
 * Does the boot asynchronously
 *
 * @param apiVersion - the current API version
 * @param config - the boot configuration
 * @returns a promise
 */
export const boot = async (apiVersion: string, config: IRcaBootConfig): Promise<void> => {
  const { language, binaryVersion } = config;

  // Initialize the trusted type manager first
  const trustedTypePolicyManager = new TrustedTypePolicyManager();
  trustedTypePolicyManager.init();

  // Load initial strings
  const stringsService = new StringsService(binaryVersion, apiVersion, (src: string) =>
    trustedTypePolicyManager.stringsPolicy.createScriptURL(src)
  );
  await stringsService.loadStringsAsync(language);

  const themeName = toThemeName(config.preferences["theme"]);
  const theme = applyTheme(themeName);

  // Inject some global styles
  mergeStyles({
    selectors: {
      ":global(body), :global(html), :global(#root)": {
        margin: 0,
        padding: 0,
        height: "100%",
        overflow: "hidden",
      },
      ":global(body)": {
        backgroundColor: theme.semanticColors.bodyBackgroundChecked,
      },
    },
  });

  registerCustomIcons();

  // We explicitly pass this CDN so that fonts are not loaded from https://spoppe-b.azureedge.net
  // which uses the public azureedge.net suffix
  initializeIcons("https://res.cdn.office.net/files/fabric/assets/icons/");

  // Configure services
  const services = {
    stringsService,
    testCatalog: new TestCatalog(apiVersion),
    settingsService: new SettingsService(apiVersion),
    testResultRepository: new TestResultRepository(apiVersion),
    proofOfWorkService: new ProofOfWorkService(apiVersion),
    oAuthService: new OAuthService(config.oauthAuthority, config.oauthClientId, config.oauthRedirectUri, config.oauthScope),
    cookieConsentService: new CookieConsentService(),
    trustedTypePolicyManager,
    antiForgeryService: new AntiforgeryService(apiVersion),
  };

  // Now render the application
  renderApp(config, services);
};
